import React, { useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { FiCheckCircle, FiRefreshCw } from "react-icons/fi";
import request from "../../utils/request";
import { getHostDetails, getHostProperties } from "../../store/thunks/HostActions";

const PMS_LABELS = {
  guesty: "Guesty",
  streamline: "Streamline",
  hospitable: "Hospitable",
  track: "Track",
};

function hasValue(value) {
  return String(value || "").trim().length > 0;
}

function isProviderConnected(host, provider) {
  if (!host) return false;
  if (provider === "guesty") return hasValue(host.guesty_client_id) && hasValue(host.guesty_client_secret);
  if (provider === "streamline") {
    return (
      (hasValue(host.streamline_api_key) && hasValue(host.streamline_api_secret)) ||
      (hasValue(host.streamline_token) && hasValue(host.streamline_token_secret))
    );
  }
  if (provider === "hospitable") return Boolean(host.hospitableConnectedAt || host.hospitableAccountId);
  if (provider === "track") return Boolean(host.trackConnectedAt || host.trackBaseUrl);
  return false;
}

function getConnectedProviders(host) {
  return ["guesty", "streamline", "hospitable", "track"].filter((provider) => isProviderConnected(host, provider));
}

function getActivePropertyCount(properties) {
  return Array.isArray(properties)
    ? properties.filter((property) => !property?.isDeleted).length
    : 0;
}

function getTrackHostSummary(summary) {
  if (Array.isArray(summary?.results)) return summary.results[0] || null;
  return summary || null;
}

export default function HostPmsIntegrationStatus({
  host,
  properties,
  setSuccessMessage,
  setErrorMessage,
}) {
  const dispatch = useDispatch();
  const [syncingProvider, setSyncingProvider] = useState("");

  const connectedProviders = useMemo(() => getConnectedProviders(host), [host]);
  const activePropertyCount = getActivePropertyCount(properties);

  const refreshHostData = async () => {
    if (!host?._id) return;
    await Promise.all([
      dispatch(getHostDetails(host._id)),
      dispatch(getHostProperties(host._id)),
    ]);
  };

  const syncProvider = async (provider) => {
    if (!host?._id) return;
    setSyncingProvider(provider);
    try {
      if (provider === "guesty") {
        const response = await request.post(`/hosts/sync-guesty-properties/${host._id}`, {}, { withCredentials: true });
        setSuccessMessage(response?.data?.msg || "Guesty properties syncing in background.");
      } else if (provider === "hospitable") {
        const response = await request.post(`/hosts/sync-hospitable-properties/${host._id}`, {}, { withCredentials: true });
        const summary = response?.data?.summary;
        const details = summary
          ? ` Created: ${summary.created || 0}, updated: ${summary.updated || 0}, skipped: ${summary.skipped || 0}, failed: ${summary.failed || 0}.`
          : "";
        setSuccessMessage(`${response?.data?.msg || "Hospitable properties synced successfully."}${details}`);
      } else if (provider === "track") {
        const propertyResponse = await request.post(
          `/hosts/sync-track-properties/${host._id}`,
          { mode: "full", dryRun: false },
          { withCredentials: true }
        );
        const reservationResponse = await request.post(
          `/hosts/sync-track-reservations/${host._id}`,
          { mode: "incremental", dryRun: false, includeAccessCodes: true },
          { withCredentials: true }
        );
        const propertySummary = getTrackHostSummary(propertyResponse?.data?.summary);
        const reservationSummary = getTrackHostSummary(reservationResponse?.data?.summary);
        setSuccessMessage(
          `Track sync complete. Properties: ${propertySummary?.created || 0} created, ${propertySummary?.updated || 0} updated, ${propertySummary?.failed || 0} failed. Reservations: ${reservationSummary?.reservationsSeen || 0} seen, ${reservationSummary?.upserts || 0} upserts, ${reservationSummary?.failed || 0} failed.`
        );
      }
      await refreshHostData();
    } catch (error) {
      setErrorMessage(
        error.response?.data?.message ||
          error.response?.data?.error ||
          error.message ||
          `Failed to sync ${PMS_LABELS[provider] || "PMS"} data.`
      );
    } finally {
      setSyncingProvider("");
    }
  };

  return (
    <section className="mt-5 rounded-2xl border border-zinc-200 bg-white p-5 shadow-sm">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-zinc-500">PMS integration</p>
          <h2 className="mt-1 text-xl font-bold text-zinc-900">Connection status</h2>
          <p className="mt-1 text-sm text-zinc-500">
            {activePropertyCount} active {activePropertyCount === 1 ? "property" : "properties"} on this host.
          </p>
        </div>
      </div>

      {connectedProviders.length === 0 ? (
        <div className="mt-4 rounded-xl border border-dashed border-zinc-200 bg-zinc-50 px-4 py-5 text-sm text-zinc-500">
          No active PMS connection is detected for this host.
        </div>
      ) : (
        <div className="mt-4 grid gap-3 md:grid-cols-2">
          {connectedProviders.map((provider) => {
            const syncing = syncingProvider === provider;
            const canSync = ["guesty", "hospitable", "track"].includes(provider);

            return (
              <div key={provider} className="rounded-xl border border-zinc-200 bg-zinc-50 p-4">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <div className="flex items-center gap-2">
                      <FiCheckCircle className="h-4 w-4 text-emerald-600" />
                      <p className="text-sm font-bold text-zinc-900">{PMS_LABELS[provider]}</p>
                    </div>
                    <p className="mt-1 text-xs font-semibold text-emerald-700">Active</p>
                  </div>
                  {canSync ? (
                    <button
                      type="button"
                      onClick={() => syncProvider(provider)}
                      disabled={Boolean(syncingProvider)}
                      className="inline-flex items-center gap-2 rounded-lg bg-zinc-900 px-3 py-2 text-xs font-bold text-white transition hover:bg-zinc-800 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      <FiRefreshCw className={`h-3.5 w-3.5 ${syncing ? "animate-spin" : ""}`} />
                      {syncing ? "Syncing" : "Sync now"}
                    </button>
                  ) : (
                    <span className="rounded-lg border border-zinc-200 bg-white px-3 py-2 text-xs font-semibold text-zinc-500">
                      No sync route
                    </span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
}
